import { Container } from '@mui/material'
import { Box } from '@mui/system'
import { useContext, useEffect, useMemo, useState } from 'react'
import { format, subDays, parse } from 'date-fns'

import { AlertComponent } from 'src/components/Alert/AlertComponent'
import { ModalPassword } from 'src/components/Modal/ModalPassword'
import DashboardViewProvider from 'src/context/DashboardViewContext'
import { useGlobalSelectedCompany } from 'src/context/GlobalSelectedCompanyContext'
import { ModalChangePasswordContext } from 'src/context/ModalChangePasswordContext'
import { getDashboardData } from 'src/service/dashboardService'

import { AccessRanking } from './Cards/AccessRanking'
import { Connections } from './Cards/Connections'
import { PerGroupConnections } from './Cards/PerGroupConnections'
import { ReconnectionsAverage } from './Cards/ReconnectionsAverage'
import { UsePerPeriod } from './Cards/UsePerPeriod'
import { Users } from './Cards/Users'
import { TotalAccessPerMonth } from './Cards/TotalAccessPerMonth'

import { DashboardHeader } from './DashboardHeader'
import { DashboardDataService } from './DashboardDataService'

export const Dashboard = () => {
  const { open, handleCloseModal } = useContext(ModalChangePasswordContext)
  const { selectedCompanyId, isGlobalAdminSelected } = useGlobalSelectedCompany()

  const dashboardService = useMemo(() => new DashboardDataService(), [])

  const today = format(new Date(), 'MM/dd/yyyy')
  const yesterday = format(subDays(new Date(), 1), 'MM/dd/yyyy')

  const [period, setPeriod] = useState('1d')
  const [data, setData] = useState<any>()
  const [initialDate, setInitialDate] = useState(yesterday)
  const [finalDate, setFinalDate] = useState(today)

  const formatForApi = (date: string) => {
    const parsed = parse(date, 'MM/dd/yyyy', new Date())
    return format(parsed, 'yyyy/MM/dd')
  }

  const fetchData = async (start = initialDate, end = finalDate) => {
    const paramsCompanyId = isGlobalAdminSelected() ? {} : { companyId: selectedCompanyId }
    const data = await getDashboardData({
      params: {
        time: period,
        dateStart: formatForApi(start),
        dateEnd: formatForApi(end),
        ...paramsCompanyId,
      },
    })
    setData(data)
  }

  const onChangeDateInitial = (date: string) => {
    setInitialDate(date)
    fetchData(date, finalDate)
  }

  const onChangeDateFinal = (date: string) => {
    setFinalDate(date)
    fetchData(initialDate, date)
  }

  useEffect(() => {
    fetchData()
  }, [period, selectedCompanyId])

  const sectionStyle = {
    maxWidth: '950px',
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '15px',
    mt: '16px',
  }

  return (
    <DashboardViewProvider>
      <Container sx={{ mt: '64px' }}>
        <Box
          sx={{
            width: '100%',
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'baseline',
            px: '28px',
            pb: '50px',
            position: 'relative',
          }}
        >
          <ModalPassword open={open} handleClose={handleCloseModal} />

          <Box sx={{ width: '100%', maxWidth: '950px', mt: 2 }}>
            <AlertComponent />
          </Box>

          <DashboardHeader
            onPeriodChange={(periodValue, _periodName, initial, final) => {
              setPeriod(periodValue)
              setInitialDate(initial)
              setFinalDate(final)
            }}
            onChangeDateInitial={onChangeDateInitial}
            onChangeDateFinal={onChangeDateFinal}
          />

          <Box sx={sectionStyle}>
            <TotalAccessPerMonth />
          </Box>

          <Box sx={sectionStyle}>
            <Connections value={dashboardService.getConnections(data)} />
            <ReconnectionsAverage
              value={dashboardService.getAverageReconnections(data)}
            />
            <PerGroupConnections
              labels={dashboardService.getConnectionsPerGroupLabels(data)}
              data={dashboardService.getConnectiosPerGroupValues(data)}
              total={dashboardService.getConnectiosPerGroupTotalValue(data)}
            />
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                gap: '16px',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <UsePerPeriod
                  labels={dashboardService.getPeriodUseLabels(data)}
                  data={dashboardService.getPeriodUseValues(data)}
                />
                <Users
                  newUsersValue={dashboardService.getNewUsers(data)}
                  returningUsersValue={dashboardService.getReturningUsers(data)}
                />
              </Box>
              <AccessRanking data={dashboardService.getAccessRankingData(data)} />
            </Box>
          </Box>
        </Box>
      </Container>
    </DashboardViewProvider>
  )
}
